import { ButtonHTMLAttributes, CSSProperties } from 'react';
import Icon from './Icon';
import { useTheme } from '../context/ThemeContext';

type Variant = 'primary' | 'secondary' | 'danger' | 'outline' | 'ghost';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  label: string;
  variant?: Variant;
  loading?: boolean;
  icon?: string;
  iconRight?: boolean;
  fullWidth?: boolean;
  style?: CSSProperties;
}

export default function Button({
  label,
  variant = 'primary',
  loading = false,
  icon,
  iconRight = false,
  fullWidth = true,
  style,
  disabled,
  ...rest
}: ButtonProps) {
  const { colors } = useTheme();
  const variants: Record<Variant, { bg: string; text: string; border: string }> = {
    primary: { bg: colors.accent, text: '#020617', border: colors.accent },
    secondary: { bg: colors.secondaryRaised, text: '#ffffff', border: colors.secondaryRaised },
    danger: { bg: colors.danger, text: '#ffffff', border: colors.danger },
    outline: { bg: 'transparent', text: colors.accentText, border: colors.accentDark },
    ghost: { bg: 'transparent', text: colors.textSub, border: 'transparent' },
  };
  const v = variants[variant];
  const isDisabled = disabled || loading;

  const baseStyle: CSSProperties = {
    padding: '14px 20px',
    borderRadius: 999,
    border: `1.5px solid ${v.border}`,
    backgroundColor: v.bg,
    color: v.text,
    width: fullWidth ? '100%' : undefined,
    minHeight: 50,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontSize: 15,
    fontWeight: 800,
    cursor: isDisabled ? 'default' : 'pointer',
    opacity: isDisabled ? 0.55 : 1,
    transition: 'opacity 120ms ease',
    fontFamily: 'inherit',
    ...style,
  };

  return (
    <button disabled={isDisabled} style={baseStyle} {...rest}>
      {loading ? (
        <span
          style={{
            width: 18,
            height: 18,
            border: `2.5px solid ${v.text}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            display: 'inline-block',
            animation: 'aga-spin 0.7s linear infinite',
          }}
        />
      ) : (
        <>
          {icon && !iconRight && <Icon name={icon} size={15} color={v.text} />}
          <span>{label}</span>
          {icon && iconRight && <Icon name={icon} size={15} color={v.text} />}
        </>
      )}
    </button>
  );
}
